import { ShieldAlert, Users, Building2, Zap } from "lucide-react";
import type { RansomwareResponse } from "../../../api/status";
import {
  formatNumber,
  formatPercent,
  formatTimeframeLabel,
  calculateShare,
} from "../../../utils/ransomwareUtils";

interface BriefingStripProps {
  data: RansomwareResponse;
}

export default function BriefingStrip({ data }: BriefingStripProps) {
  const summary = data?.summary || {
    attacks: 0,
    actors: 0,
    topSector: "None",
    topSectorPct: 0,
    topSectorCount: 0,
    peakDay: null,
  };
  const daily = Array.isArray(data?.daily) ? data.daily : [];
  const totalAttacks = summary.attacks || 0;
  const totalActors = summary.actors || (Array.isArray(data?.actors) ? data.actors.length : 0);

  // Top Sector Share calculation fix (never blank or fake 0.0%)
  let topSectorPct = summary.topSectorPct;
  if (!topSectorPct && summary.topSectorCount && totalAttacks > 0) {
    topSectorPct = calculateShare(summary.topSectorCount, totalAttacks);
  }

  // Draw clean mini-sparkline directly from the daily series
  const sparklineCounts = daily.map((d) => (typeof d?.count === "number" ? d.count : 0));
  const maxSparkCount = sparklineCounts.length > 0 ? Math.max(...sparklineCounts) : 1;
  const validMax = isFinite(maxSparkCount) && maxSparkCount > 0 ? maxSparkCount : 1;

  const sparklinePoints = sparklineCounts.length > 1
    ? sparklineCounts
        .map((val, idx) => {
          const x = (idx / (sparklineCounts.length - 1)) * 80;
          const y = 22 - (val / validMax) * 18;
          return `${x.toFixed(1)},${y.toFixed(1)}`;
        })
        .join(" ")
    : "0,22 80,22";

  return (
    <div
      className="rw-briefing-strip"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        background: "var(--dash-card-bg, rgba(15, 23, 42, 0.6))",
        border: "1px solid var(--dash-card-border, rgba(255, 255, 255, 0.08))",
        borderRadius: 12,
        overflow: "hidden",
        backdropFilter: "blur(12px)",
      }}
    >
      {/* STAT 1: TOTAL INCIDENTS */}
      <div
        className="rw-strip-stat"
        style={{
          padding: "20px 24px",
          borderRight: "1px solid var(--dash-card-border, rgba(255, 255, 255, 0.08))",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <span
            style={{
              fontSize: "0.72rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: "var(--dash-text-muted, #94a3b8)",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <ShieldAlert size={14} style={{ color: "var(--dash-indigo, #6366F1)" }} /> Total Incidents
          </span>

          {/* Sparkline from exact daily series */}
          {sparklineCounts.length > 0 && (
            <svg width="80" height="24" style={{ overflow: "visible" }} aria-hidden="true">
              <polyline
                fill="none"
                stroke="var(--dash-indigo, #6366F1)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                points={sparklinePoints}
              />
            </svg>
          )}
        </div>

        <div style={{ marginTop: 4 }}>
          <div
            style={{
              fontSize: "2.2rem",
              fontWeight: 800,
              fontVariantNumeric: "tabular-nums",
              lineHeight: 1,
              color: "var(--dash-text-primary, #f8fafc)",
            }}
          >
            {formatNumber(totalAttacks)}
          </div>
        </div>

        <div style={{ fontSize: "0.75rem", color: "var(--dash-text-muted, #94a3b8)", marginTop: 8 }}>
          Disclosures in last {formatTimeframeLabel(data?.timeframe)}
        </div>
      </div>

      {/* STAT 2: ACTIVE SYNDICATES */}
      <div
        className="rw-strip-stat"
        style={{
          padding: "20px 24px",
          borderRight: "1px solid var(--dash-card-border, rgba(255, 255, 255, 0.08))",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <span
            style={{
              fontSize: "0.72rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: "var(--dash-text-muted, #94a3b8)",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Users size={14} style={{ color: "var(--dash-indigo, #6366F1)" }} /> Active Syndicates
          </span>
        </div>

        <div style={{ marginTop: 4 }}>
          <div
            style={{
              fontSize: "2.2rem",
              fontWeight: 800,
              fontVariantNumeric: "tabular-nums",
              lineHeight: 1,
              color: "var(--dash-text-primary, #f8fafc)",
            }}
          >
            {formatNumber(totalActors)}
          </div>
        </div>

        <div style={{ fontSize: "0.75rem", color: "var(--dash-text-muted, #94a3b8)", marginTop: 8 }}>
          {totalActors > 10 ? `Top 10 of ${totalActors} groups listed` : `${totalActors} active threat actor${totalActors === 1 ? "" : "s"}`}
        </div>
      </div>

      {/* STAT 3: TOP TARGET SECTOR */}
      <div
        className="rw-strip-stat"
        style={{
          padding: "20px 24px",
          borderRight: "1px solid var(--dash-card-border, rgba(255, 255, 255, 0.08))",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <span
            style={{
              fontSize: "0.72rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: "var(--dash-text-muted, #94a3b8)",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Building2 size={14} style={{ color: "var(--dash-indigo, #6366F1)" }} /> Top Target Sector
          </span>
        </div>

        <div style={{ marginTop: 4 }}>
          <div
            style={{
              fontSize: "1.25rem",
              fontWeight: 800,
              color: "var(--dash-text-primary, #f8fafc)",
              lineHeight: 1.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={summary.topSector || "None"}
          >
            {summary.topSector || "None"}
          </div>

          {topSectorPct ? (
            <div
              style={{
                fontSize: "0.78rem",
                fontWeight: 700,
                color: "var(--dash-indigo, #818cf8)",
                marginTop: 4,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {formatPercent(topSectorPct)} of total disclosures
            </div>
          ) : null}
        </div>

        <div style={{ fontSize: "0.75rem", color: "var(--dash-text-muted, #94a3b8)", marginTop: 8 }}>
          Highest risk industry concentration
        </div>
      </div>

      {/* STAT 4: PEAK ACTIVITY DATE */}
      <div
        className="rw-strip-stat"
        style={{
          padding: "20px 24px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <span
            style={{
              fontSize: "0.72rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: "var(--dash-text-muted, #94a3b8)",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Zap size={14} style={{ color: "#f59e0b" }} /> Peak Activity Date
          </span>
        </div>

        <div style={{ marginTop: 4 }}>
          <div
            style={{
              fontSize: "1.25rem",
              fontWeight: 800,
              color: "var(--dash-text-primary, #f8fafc)",
              lineHeight: 1.2,
            }}
          >
            {summary.peakDay?.label || summary.peakDay?.date || "N/A"}
          </div>

          <div
            style={{
              fontSize: "0.78rem",
              fontWeight: 700,
              color: "#f59e0b",
              marginTop: 4,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {summary.peakDay ? `${formatNumber(summary.peakDay.count)} incidents in 24h` : "No peak recorded"}
          </div>
        </div>

        <div style={{ fontSize: "0.75rem", color: "var(--dash-text-muted, #94a3b8)", marginTop: 8 }}>
          Highest single-day incident volume
        </div>
      </div>
    </div>
  );
}
